import {
	cache,
	getDefaultGrowPrivateRoute,
	InitializationStep,
	ZomentumLoginRoutes,
	ZomentumRoutes,
	ZomentumStorageKeys,
	ZomentumWindow
} from "@/Contracts";
import { safeRequestIdleCallback } from "@/Utils/safeRequestIdleCallback";
import React, { useEffect, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { notification, Tooltip } from "antd";
import useDispatch from "@/Components/Utils/useDispatch";
import LoginActionCreators from "@/Actions/Login";
import {
	RouteComponentProps,
	useRouteMatch,
	withRouter
} from "react-router-dom";
import { AuthToken } from "@/Contracts/Login";
import {
	isDocumentCustomerViewUrl,
	isDocumentPdfViewUrl,
	isVerifyPaymentViewUrl
} from "@/Contracts/Utils";
import initializeTinyMCE from "@/Utils/initializeTinyMCE";
import UsersActionCreators from "@/Actions/User";
import { BillingStatusEnum, UserData } from "@/Contracts/Users";
import NotificationFeedActionCreators from "@/Actions/Others/NotificationFeed";
import BannerNotificationActionCreators from "@/Actions/BannerNotification";
import { timezones } from "@/Contracts/Settings";
import { setUser as SentrySetUser } from "@sentry/react";

import { UIConfigActionCreators } from "@/Actions/UI";
import filter from "lodash/filter";
import {
	useComponentDidUpdate,
	useMobileView,
	useNotForPdfView
} from "@/Hooks";
import ZMessage from "@molecules/ZMessage";
import { useFeatureFlag } from "@/Hooks/useFeature";
import { useAuth0 } from "@auth0/auth0-react";
import publicIp from "public-ip";
import { GlobalState } from "@/Reducers/interface";
import { Steps } from "@/Onboarding/contracts";
import { useGuideHook } from "@/Onboarding/hooks/useOnboardingGuide";
import { useInitOnboarding } from "@/Onboarding/hooks/useInitOnboarding";
import { useCheckListGuide } from "@/Onboarding/components/OnBoardingBar";
import { onbordingStore, useOnboardingSelector } from "@/Onboarding/store";
import { OnboardingState } from "@/Onboarding/store/reducers";
import ZCreditCardModal from "@molecules/ZCreditCardModal";
import PaymentModal from "@/Components/UI/PaymentModal/AddCardModal";
import ZButton from "@atoms/ZButton";

import jwt_decode from "jwt-decode";
import AnnouncementActionCreators from "@/Actions/Announcement";
import useAnnouncementModal from "@/V2Components/pages/Announcement";

declare let window: ZomentumWindow;

type Props = RouteComponentProps;
type UnregisterCallback = () => void;

interface UserAuth {
	roles: string[];
	user_id: string;
}

interface Auth0TokenInterface {
	aud: string[];
	azp: string;
	exp: number;
	"https://channelforce.com/user_authorization"?: UserAuth;
	"https://zomentum.com/user_authorization"?: UserAuth;
	iat: number;
	iss: string;
	permissions: string[];
	scope: string;
	sub: string;
}

const LoadThirdPartyScripts: React.FC<Props> = props => {
	const showPartnerAlignFeaturesFlag = useFeatureFlag(
		"show_partner_align_features"
	);
	const { notForPdfView } = useNotForPdfView();
	const dispatch = useDispatch();
	const auth0 = useAuth0();
	const {
		userData,
		isLoggedIn,
		userCompany,
		configurationSettings,
		billingInfo,
		companyUserList,
		subscriptions,
		isPaymentNeeded,
		isAuthenticatedFromSignIn,
		announcement
	} = useSelector((state: GlobalState) => {
		return {
			userData: state.user.userData,
			isLoggedIn: state.login.isLoggedIn,
			userCompany: state.user.userCompany,
			configurationSettings: state.user.configurationSettings,
			billingInfo: state.user.billingInfo,
			companyUserList: state.user.companyUserList,
			subscriptions: state.user.subscriptions,
			isPaymentNeeded:
				state.user?.billingInfo?.payment_source === null
					? null
					: !!!state.user.billingInfo.payment_source,
			isAuthenticatedFromSignIn: state.login.isAuthenticatedFromSignIn,
			isLoading: state.login.isLoading,
			announcement: state.announcement.announcement
		};
	}, shallowEqual);
	const unlisten = useRef<UnregisterCallback | null>(null);
	const isDashboardInitialized = useRef(false);
	const isOnboardingEnabled = useFeatureFlag("in_app_onboarding");
	const [isOnboardingIntiated, setIsOnboardingInitated] = useState(false);
	const { showAnnouncementModal, AnnouncementModal } = useAnnouncementModal();
	const [showTimezoneNotification, setShowTimezoneNotification] =
		useState(false);

	const isCheckListActive = useOnboardingSelector(
		(state: OnboardingState) => state.checkListBarData?.is_checklist_active
	);

	const { initOnboarding } = useInitOnboarding();
	const isDocumentDetailPage = useRouteMatch(
		ZomentumRoutes.DocumentsDetail
	)?.isExact;
	const isDocumentPreviewPage = useRouteMatch(
		ZomentumRoutes.DocumentsPreview
	)?.isExact;
	const isDocumentPDFPage = useRouteMatch(
		ZomentumRoutes.DocumentsPdfView
	)?.isExact;
	const isNotDocumentPage = !(
		isDocumentDetailPage ||
		isDocumentPreviewPage ||
		isDocumentPDFPage
	);
	const { OnboardingCheckList } = useCheckListGuide(
		!!isCheckListActive && isOnboardingEnabled && isNotDocumentPage
	);

	const initializeOnboarding = () => {
		const isPaymentNeeded =
			billingInfo?.payment_source === null
				? null
				: !!!billingInfo.payment_source;

		if (isPaymentNeeded !== null) {
			initOnboarding(isAuthenticatedFromSignIn, isPaymentNeeded);
			return;
		}

		if (billingInfo.is_internal) {
			initOnboarding(isAuthenticatedFromSignIn);
			return;
		}
	};

	useEffect(() => {
		if (isOnboardingIntiated && isOnboardingEnabled && isNotDocumentPage) {
			initializeOnboarding();
		}
	}, [isOnboardingIntiated, isOnboardingEnabled, billingInfo]);

	useEffect(() => {
		if (isCheckListActive) {
			notification.close("timezoneAlert");
			window?.Beamer?.closeAnnouncementBar?.();
		}
	}, [isCheckListActive]);

	const preloadCurrentApiUrl = () => {
		if (process.env.REACT_APP_API_HOST) {
			const res = document.createElement("link");
			res.rel = "preconnect";
			res.as = "href";
			res.href = process.env.REACT_APP_API_HOST;
			document.head.appendChild(res);
		}
	};

	const updateThirdPartyScripts = async () => {
		await updateAuth0();
		updateSentry();
		updateBeamer();
		updateFreshchat();
	};

	const updateFreshchat = () => {
		if (
			// process.env.REACT_APP_NODE_ENV === 'production' &&
			process.env.REACT_APP_FRESHCHAT &&
			window.fcWidget &&
			userData &&
			companyUserList
		) {
			try {
				window.fcWidget.init({
					token: process.env.REACT_APP_FRESHCHAT,
					host: "https://wchat.freshchat.com",
					externalId: userData.id,
					restoreId: userData.fc_token,
					config: {
						headerProperty: {
							direction: "ltr",
							left: "5px",
							hideChatButton: true
						},
						content: {
							actions: {
								csat_yes: "No, rate agent",
								csat_no: "Continue chat"
							},
							headers: {
								csat_question: "Did we address your concerns?",
								csat_yes_question:
									"How would you rate this interaction?",
								csat_no_question:
									"How could we have helped better?",
								csat_thankyou: "Thanks for the response",
								csat_rate_here: "Submit your rating here"
							}
						}
					},
					tags: ["product"]
				});
				window.fcWidget.user.get(async (getUserResponse: any) => {
					const getUserStatus =
						getUserResponse && getUserResponse.status;
					if (getUserStatus !== 200) {
						const { name, email, phone } = userData;
						const { billing_status } = billingInfo;

						window.fcWidget.user.setProperties({
							firstName: name?.first || "",
							lastName: name?.last || "",
							email,
							phone,
							paid_customer:
								billing_status === BillingStatusEnum.Active
						});

						window.fcWidget.on(
							"user:created",
							async (createUserResponse: any) => {
								const createUserStatus =
									createUserResponse &&
									createUserResponse.status;
								const createUserData =
									createUserResponse &&
									createUserResponse.data;
								if (createUserStatus === 200) {
									if (createUserData.restoreId) {
										const currentCompanyUserList: UserData[] =
											[...companyUserList];
										try {
											const updateCompanyUserIndex =
												currentCompanyUserList.findIndex(
													companyUser =>
														companyUser.id ===
														userData.id
												);
											if (updateCompanyUserIndex > -1) {
												const updateCompanyUserRequest: UserData =
													{
														...currentCompanyUserList[
															updateCompanyUserIndex
														]
													};
												updateCompanyUserRequest.fc_token =
													createUserData.restoreId;
												const updateUserDataRequest = {
													...userData
												};
												updateUserDataRequest.fc_token =
													updateCompanyUserRequest.fc_token;
												dispatch(
													UsersActionCreators.updateUserData(
														updateUserDataRequest
													)
												);
												dispatch(
													UsersActionCreators.updateCompanyUser(
														updateCompanyUserRequest
													)
												);
											}
										} catch (error) {
											console.error(error);
										}
									}
								}
							}
						);
					}
				});
				window.fcWidget.on("unreadCount:notify", (response: any) => {
					if (response)
						dispatch(
							UIConfigActionCreators.getFreshChatMessageStatus(
								response.count ?? 0
							)
						);
				});
			} catch (error) {
				console.error(error);
			}
		}
	};

	const beamerCountCallback = (count: number): void => {
		dispatch(UIConfigActionCreators.getBeamerIconStatus(count));
	};

	const updateBeamer = () => {
		window.Beamer &&
			window.Beamer.update({
				user_lastname: userData.name.last,
				user_firstname: userData.name.first,
				user_email: userData.email,
				user_id: userData.id,
				callback: beamerCountCallback
			}) &&
			window.Beamer.init();
		setTimeout(() => {
			const isCheckListActive =
				onbordingStore.getState().checkListBarData?.is_checklist_active;
			if (isCheckListActive) {
				window?.Beamer?.closeAnnouncementBar?.();
			}
		}, 1000);
	};

	const updateSentry = () => {
		try {
			if (!!userData.id?.length) {
				SentrySetUser({
					id: userData.id,
					email: userData.email,
					username: userData.name.full ?? ""
				});
			}
		} catch (error) {
			console.error(error);
		}
	};

	const updateAuth0 = async () => {
		const auth0CheckSessionInterval = setInterval(async () => {
			try {
				const token = await auth0.getAccessTokenSilently();
				cache.save(ZomentumStorageKeys.Token, new AuthToken(token));
				dispatch(LoginActionCreators.refreshTokenSuccessful());
			} catch (error) {
				console.error("Refresh token is failing with error", error);
				clearInterval(auth0CheckSessionInterval);
				dispatch(LoginActionCreators.refreshTokenFailed());
			}
		}, 2 * 60 * 60 * 1000);
	};

	const getAllUser = async () => {
		try {
			dispatch(UsersActionCreators.showUserLoader());

			const [billingResult] = await Promise.all([
				dispatch(UsersActionCreators.getBillingInfo()),
				dispatch(UsersActionCreators.getTaxRegionsList()),
				dispatch(UsersActionCreators.getTaxCategoriesList()),
				dispatch(UsersActionCreators.fetchAllEmailSignatures())
			]);

			const { billingInfo } = billingResult;

			if (!subscriptions.length) {
				const { subscriptions } = await dispatch(
					UsersActionCreators.getSubscriptionPlans()
				);
				const noCurrentPlan = !subscriptions.some(
					plan => plan.current_plan
				);
				if (
					noCurrentPlan &&
					!billingInfo.is_internal &&
					!!billingInfo.payment_source
				) {
					await dispatch(
						UsersActionCreators.getSubscriptionDetails()
					);
				}
			}
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(UsersActionCreators.hideUserLoader());
		}
	};

	const getNotificationData = async () => {
		try {
			dispatch(
				NotificationFeedActionCreators.showNotificationFeedLoader()
			);

			const feedActionResult = await dispatch(
				NotificationFeedActionCreators.getNotificationFeed()
			);
			const notifications = feedActionResult.notificationFeedActivityList;

			await Promise.all([
				dispatch(
					NotificationFeedActionCreators.subscribeNotificationFeedActivity()
				),
				dispatch(BannerNotificationActionCreators.getBannerFeed()),
				dispatch(
					BannerNotificationActionCreators.subscribeBannerDetails()
				)
			]);

			const isInitialSyncEventRelated = notifications.some(
				n => n.title === "Initial sync event"
			);

			if (isInitialSyncEventRelated) {
				await dispatch(UsersActionCreators.getUserCompany());
			}
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(
				NotificationFeedActionCreators.hideNotificationFeedLoader()
			);
		}
	};

	const initializeDashboard = async () => {
		if (isDashboardInitialized.current) return;
		const authToken = cache.get(
			ZomentumStorageKeys.Token,
			true
		) as AuthToken;
		if ((authToken && authToken.token) || isLoggedIn) {
			try {
				setIsOnboardingInitated(true);
				dispatch(LoginActionCreators.loginFromSession());
				await notForPdfView(getAllUser);

				await Promise.all([
					notForPdfView(getNotificationData),
					notForPdfView(updateThirdPartyScripts)
				]);

				const userIpAddress = await publicIp.v4();
				cache.save(ZomentumStorageKeys.UserIp, userIpAddress);

				if (ZomentumLoginRoutes.indexOf(props.location.pathname) > -1) {
					props.history.push(
						getDefaultGrowPrivateRoute(showPartnerAlignFeaturesFlag)
					);
				}

				isDashboardInitialized.current = true;
			} catch (error) {
				console.error(error);
			}
		} else {
			const isPreviewRoute =
				isDocumentCustomerViewUrl(props.location.pathname) ||
				isDocumentPdfViewUrl(props.location.pathname) ||
				isVerifyPaymentViewUrl(props.location.pathname);

			if (
				ZomentumLoginRoutes.indexOf(props.location.pathname) === -1 &&
				props.location.pathname !== ZomentumRoutes.Error &&
				!isPreviewRoute &&
				props.location.pathname !== "/" &&
				props.location.pathname !== ""
			) {
				cache.deleteAll();
				props.history.push(ZomentumRoutes.Login);
			}
		}
	};

	const checkTimeOffset = () => {
		const today = new Date();
		const userOffset = today.getTimezoneOffset();
		const serverOffset = userCompany?.settings?.time_zone_offset;
		const newTimezone: any = filter(timezones, timezone => {
			if (timezone.offset === userOffset) return timezone;
			return;
		});
		const newTimezoneName = newTimezone[0].name;
		const key = "timezoneAlert";
		const btn = (
			<div>
				<ZButton
					type="default"
					onClick={() => {
						notification.close("timezoneAlert");
					}}
				>
					Remind me Later
				</ZButton>
				<Tooltip title={`Change to ${newTimezoneName}`}>
					<ZButton
						type="primary"
						onClick={() => {
							handleTimezoneChange(userOffset);
						}}
						className="margin-left-8px"
					>
						Update Now
					</ZButton>
				</Tooltip>
			</div>
		);

		if (
			!isCheckListActive &&
			userOffset !== serverOffset &&
			userData?.user_settings?.time_zone_display_browser_pop_up
		) {
			notification.open({
				message: "Attention",
				description:
					"Your detected browser timezone does not match the one set in your profile. To ensure best experience, please update your timezone",
				duration: 10,
				btn,
				key,
				type: "warning",
				placement: "bottomRight",
				onClose: handleTimezoneLater
			});
			setShowTimezoneNotification(true);
		}
	};

	const handleTimezoneChange = async (offset: number) => {
		const newTimezone: any = filter(timezones, timezone => {
			if (timezone.offset === offset) return timezone;
			return;
		});
		const value = newTimezone[0].name;
		try {
			const editCompanyUserRequest = userCompany;
			editCompanyUserRequest.settings.time_zone = value;
			await dispatch(
				UsersActionCreators.editUserCompany(editCompanyUserRequest)
			);
			ZMessage.success("Timezone has been changed to " + value);
		} catch (error) {
			console.error(error);
		} finally {
			notification.close("timezoneAlert");
		}
	};

	const handleTimezoneLater = async () => {
		notification.close("timezoneAlert");
		try {
			const editCompanyUserRequest = userData;
			editCompanyUserRequest.user_settings.time_zone_display_browser_pop_up =
				false;
			await dispatch(
				UsersActionCreators.editUserData(editCompanyUserRequest)
			);
		} catch (error) {
			console.error(error);
		}
	};

	/* 
		In case of the in-app onboarding of internal accounts, this method sets a flag in login store which indicates that the user is coming from /setup-account i.e first login after signup.
		This flag is used to display the welcome video when the onboarding initialises.  

		It's called before the onboarding initialises to prevent a race condition.
	*/
	const authenticateFromLogin = () => {
		const isRouteSetupAccount =
			props.history.location.pathname === ZomentumRoutes.SetupAccount;
		if (
			isRouteSetupAccount &&
			(billingInfo.is_internal ||
				configurationSettings.company_initialization_step ===
					InitializationStep.PaymentScreenPresented)
		) {
			dispatch(LoginActionCreators.authenticateFromLogin());
		}
	};
	const initializeScripts = async () => {
		const oldAuthToken = cache.get(
			ZomentumStorageKeys.Token,
			true
		) as AuthToken;

		if (oldAuthToken && oldAuthToken.token) {
			const decodedToken: Auth0TokenInterface = jwt_decode(
				oldAuthToken.token
			);
			const currentTimeStamp = new Date().getTime();
			const tokenExpiryTime = decodedToken.exp * 1000;

			if (tokenExpiryTime < currentTimeStamp) {
				try {
					const token = await auth0.getAccessTokenSilently();
					cache.save(ZomentumStorageKeys.Token, new AuthToken(token));
					dispatch(LoginActionCreators.refreshTokenSuccessful());
				} catch (error) {
					console.error(
						"Logging out user as the session token is expired"
					);
					dispatch(LoginActionCreators.logout(auth0));
				}
			}
		}

		const currentAuthToken = cache.get(
			ZomentumStorageKeys.Token,
			true
		) as AuthToken;

		if (currentAuthToken && currentAuthToken.token) {
			authenticateFromLogin();
			initializeDashboard();
		}

		preloadCurrentApiUrl();
		safeRequestIdleCallback(initializeTinyMCE);

		await notForPdfView(() => {
			safeRequestIdleCallback(initializeBeamer);
		});
	};

	const triggerHubSpot = async () => {
		if (userData.email && isLoggedIn) {
			await notForPdfView(initializeCustomerchat);
		}
	};

	useEffect(() => {
		triggerHubSpot();
	}, [userData.email, isLoggedIn, props.location.pathname]);

	useEffect(() => {
		if (
			!!userCompany?.id?.length &&
			!showTimezoneNotification &&
			isAuthenticatedFromSignIn
		)
			checkTimeOffset();
	}, [userCompany]);

	const getAnnouncement = async () => {
		try {
			await dispatch(AnnouncementActionCreators.getAnnouncement());
		} catch (e) {
			console.error(e);
		}
	};

	useEffect(() => {
		if (isLoggedIn) {
			notForPdfView(getAnnouncement);
		}
	}, [isLoggedIn]);

	const handleAnnouncementModal = async () => {
		try {
			setTimeout(() => {
				showAnnouncementModal(true);
			}, 7000);
		} catch (e) {
			console.error(e);
		}
	};

	useEffect(() => {
		if (announcement) {
			handleAnnouncementModal();
		}
	}, [announcement]);

	const isMobileView = useMobileView();

	const isDocumentPreview = useRouteMatch(
		ZomentumRoutes.DocumentsPreview
	)?.isExact;

	const initializeCustomerchat = async () => {
		if (isMobileView) {
			return;
		}
		if (isDocumentPreview) {
			return;
		}
		if (process.env.REACT_APP_FRESHCHAT) {
			const existingScript = document.getElementById(
				"freshchat-initialization"
			);
			if (!existingScript) {
				safeRequestIdleCallback(() => {
					const script: HTMLScriptElement =
						document.createElement("script");
					script.src = "https://wchat.freshchat.com/js/widget.js";
					script.id = "freshchat-initialization";
					document.body.appendChild(script);
				});
			}
		}
	};

	const initializeBeamer = () => {
		const existingScript = document.getElementById("beamer-initialization");
		if (
			!existingScript &&
			!isDocumentCustomerViewUrl(window.location.pathname) &&
			!isDocumentPdfViewUrl(window.location.pathname) &&
			!isVerifyPaymentViewUrl(window.location.pathname)
		) {
			const script: HTMLScriptElement = document.createElement("script");
			script.src = "https://app.getbeamer.com/js/beamer-embed.js";
			script.id = "beamer-initialization";
			document.body.appendChild(script);
		}
	};

	useEffect(() => {
		initializeScripts();

		return () => {
			unlisten.current?.();

			if (
				// process.env.REACT_APP_NODE_ENV === 'production' &&
				process.env.REACT_APP_FRESHCHAT &&
				window.fcWidget
			) {
				window.fcWidget.destroy();
			}
		};
	}, []);

	// Call the initializeDashboard when the token is set to localStorage
	useEffect(() => {
		const initDashBoard = () => {
			initializeDashboard();
		};
		document.addEventListener("InitializeDashboard", initDashBoard);
		return () => {
			document.removeEventListener("InitializeDashboard", initDashBoard);
		};
	}, []);

	useComponentDidUpdate(() => {
		if (
			props.location.pathname !== ZomentumRoutes.Error &&
			props.location.pathname !== ZomentumRoutes.Authorize &&
			!userData.id
		) {
			initializeDashboard();
		}
	}, [props.location.pathname]);

	useEffect(() => {
		updateFreshchat();
	}, [window.fcWidget]);

	useEffect(() => {
		updateBeamer();
	}, [userData]);

	const { GuideUI: WelcomeVideoModal } = useGuideHook({
		stageId: null,
		stepId: Steps.WELCOME_VIDEO
	});

	const CreditCardPaymentModal =
		isOnboardingEnabled && isPaymentNeeded === null
			? ZCreditCardModal
			: PaymentModal;

	return (
		<>
			<CreditCardPaymentModal />
			{!isPaymentNeeded ? WelcomeVideoModal : null}
			{OnboardingCheckList}
			{AnnouncementModal}
		</>
	);
};

export default withRouter(LoadThirdPartyScripts);
